import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SettingsContext from "~/contexts/SettingsContext";
import styles from "./AssetModal.module.css";
import ThemeContext from "~/contexts/ThemeContext";
import Dialog from "~/components/Dialog/Dialog";
import CircleCloseButton from "~/icons/CircleCloseIcon";
import AssetView from "~/modules/loyalty/components/AssetView/AssetView";
import OrderContext from "~/contexts/OrderContext";
import pluralize from "pluralize";

import Spinner from "~/components/Spinner/Spinner";
import { useFetcher } from "@remix-run/react";
import { KEYS, LABELS } from "~/modules/checkout/constants";
import { action } from "~/routes/$orderSource.$siteId.($table)";
import { action as assetAction } from "~/routes/loyalty.purchaseAsset";
import AuthContext from "~/contexts/AuthContext";
import clsx from "clsx";
import { Asset, AssetInfo } from "~/entities/authEntities";
import { formatLoyaltyBalance } from "~/helpers/numberHelpers";
import { getCooldownMessage } from "~/helpers/promotionHelpers";
import ProgressClubPoints from "../../components/ProgressClubPoints/ProgressClubPoints";
import {
  LoyaltyDialogSubtitleFontSize,
  LoyaltyDialogTitleFontSize,
} from "~/theme/models/themeModels";
import { capitalizeWords } from "~/helpers/stringHelpers";
import {
  cartIsValid,
  generateLine,
  getLineBeforeDiscountPrice,
} from "~/helpers/cartHelpers";
import { sum } from "~/helpers/arrayHelpers";
import { getProductsList } from "~/helpers/menuHelpers";
import ProductItem from "~/modules/order/components/ProductItem/ProductItem";
import MenuContext from "~/contexts/MenuContext";
import { CartLine } from "~/entities/orderEntities";
import { useOutsideClick } from "~/hooks/useOutsideClick";
import ProductModal from "~/modules/order/containers/ProductModal/ProductModal";
import BackArrowIcon from "~/icons/BackArrowIcon";

type PromoModalProps = {
  visible: boolean;
  setVisible: (x: boolean) => void;
  assetInfo?: AssetInfo;
  loading?: boolean;
  error?: string;
};

enum SubmittingAction {
  None,
  Purchase,
  AssetSelection,
  CartModification,
}

const AssetModal: React.FC<PromoModalProps> = ({
  visible,
  setVisible,
  assetInfo,
  loading,
  error,
}) => {
  const { orgSettings } = useContext(SettingsContext);
  const { template } = useContext(ThemeContext);
  const {
    redeemPromotion,
    orderSummary,
    promotionsRequiringSelection,
    addOrderLine,
  } = useContext(OrderContext);
  const { authenticatedUser, useComoAuth } = useContext(AuthContext);
  const { menu } = useContext(MenuContext);

  const fetcher = useFetcher<typeof action>({ key: KEYS.SUBMIT_ORDER_FETCHER });
  const assetFetcher = useFetcher<typeof assetAction>();

  const [submitting, setSubmitting] = useState<SubmittingAction>(
    SubmittingAction.None
  );
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  // Styles
  const backgroundColor = template.menuScreen.general.backgroundColour;
  const titleFontColor = template.loyaltyDialog.general.titleFontColour;
  const titleFontSize =
    LoyaltyDialogTitleFontSize[template.loyaltyDialog.general.titleFontSize];
  const subtitleFontColor = template.loyaltyDialog.general.subtitleFontColour;
  const subtitleFontSize =
    LoyaltyDialogSubtitleFontSize[
      template.loyaltyDialog.general.subtitleFontSize
    ];
  const showTargetPoints = template.loyaltyDialog.targetPoints.show;
  const targetPoints = template.loyaltyDialog.targetPoints.points;

  // Points
  const pointsTextColor = template.loyaltyDialog.general.pointsFontColour;

  const promoName = orgSettings?.loyaltySettings?.promotionName ?? "Promotion";

  const fetcherErrorMessage = useMemo(() => {
    const isError =
      fetcher.data && !fetcher?.data?.success && fetcher.state === "idle";

    if (!isError) return null;

    if (!useComoAuth) {
      return fetcher.data?.message || "Something went wrong.";
    }

    const cooldownMessage = getCooldownMessage(promoName);

    return cooldownMessage || fetcher.data?.message || "Something went wrong.";
  }, [fetcher.data, fetcher.state, useComoAuth, promoName]);

  const showAssetFetcherErrorMessage = useMemo(
    () =>
      assetFetcher.data &&
      !assetFetcher?.data?.success &&
      assetFetcher.state == "idle",
    [assetFetcher.data, assetFetcher.state]
  );

  const purchasedAsset = useMemo(
    () =>
      assetInfo?.assets?.find(
        (asset) => asset.key === assetFetcher.data?.assetKey
      ),
    [assetFetcher.data, assetInfo?.assets]
  );

  const promotionOverrideExists = Object.values(
    orderSummary.promotionSchedulingOverride
  ).some((x) => x);

  const appliedAsset = useMemo(
    () =>
      // Don't show applied asset if there was a redemption key used
      !orderSummary.redemptionKey || promotionOverrideExists
        ? // Don't show applied asset if any promotion scheduling override is set (this means the promotion exists in dnero and a separate flow is used)
          undefined
        : assetInfo?.assets?.find(
            (asset) => asset.key === orderSummary.redemptionKey
          ),
    [orderSummary.redemptionKey, assetInfo?.assets, promotionOverrideExists]
  );

  const productsSummary = useMemo(() => {
    const qtyBySku: Record<string, number> = {};
    const priceBySku: Record<string, number> = {};

    orderSummary.lines.forEach((line) => {
      const sku = line.item.product.sku;
      if (sku) {
        qtyBySku[sku] = (qtyBySku[sku] ?? 0) + line.item.qty;
        priceBySku[sku] =
          (priceBySku[sku] ?? 0) + getLineBeforeDiscountPrice(line);
      }
    });

    return { qtyBySku, priceBySku };
  }, [orderSummary.lines]);

  const [addingLine, setAddingLine] = useState<CartLine | null>(null);

  const productModalRef = useRef(null);

  useOutsideClick([productModalRef], () => setAddingLine(null));

  const [hideRequirementSelection, setHideRequirementSelection] =
    useState(false);

  // Calculate the next selection that must be made for the currently selected asset
  const currentSelectionRequirement: {
    subTitle: string;
    text: string;
    productSkus?: string[];
    productCodes?: string[];
  } | null = useMemo(() => {
    if (
      appliedAsset &&
      appliedAsset.selectionRules &&
      appliedAsset.selectionRules.length
    ) {
      // Find rules which need selections
      for (const rules of appliedAsset.selectionRules) {
        if (rules.logic !== "and") {
          console.error(`Unhandled como selection rule logic: ${rules.logic}`);
          continue;
        }

        for (const requirement of rules.requirements) {
          const totalQty = sum(
            requirement.productSkus.map(
              (sku) => productsSummary.qtyBySku[sku] ?? 0
            )
          );

          const totalPrice = sum(
            requirement.productSkus.map(
              (sku) => productsSummary.priceBySku[sku] ?? 0
            )
          );

          if (
            totalQty < requirement.requiredQty ||
            totalPrice < requirement.requiredPrice
          ) {
            return {
              subTitle: appliedAsset.name,
              text: requirement.text,
              productSkus: requirement.productSkus,
            };
          }
        }
      }
    }

    const promotionRequiringSelection = promotionsRequiringSelection.find(
      (x) => x.allowModal
    );
    const selectionsRemaining = promotionRequiringSelection?.requiredSelections;

    if (selectionsRemaining) {
      const promotion = promotionRequiringSelection.promotion;

      return {
        subTitle: appliedAsset?.name || promotion.name,
        text: selectionsRemaining.requiredQtyRemaining
          ? "Select required item(s)"
          : "Select reward(s)",
        productCodes:
          (selectionsRemaining.requiredQtyRemaining
            ? promotion.requiredProductCodes
            : promotion.productCodes) ?? [],
      };
    }

    return null;
  }, [appliedAsset, productsSummary, promotionsRequiringSelection]);

  const onClose = useCallback(() => {
    setSelectedAsset(null);
    setSubmitting(SubmittingAction.None);
    setVisible(false);
    setAddingLine(null);
    setHideRequirementSelection(false);
  }, [setVisible]);

  const isSelectionRequired = !!currentSelectionRequirement;

  // Auto close modal when...
  useEffect(() => {
    if (
      // We have just tried to add an asset
      (submitting === SubmittingAction.AssetSelection &&
        // No in progress api requests
        fetcher.state == "idle" &&
        // Last request to order api was a success
        fetcher?.data?.success &&
        // You didn't have a purchaseable asset selected
        !selectedAsset &&
        // There's not currently a selection required for this asset
        !isSelectionRequired) ||
      // We have just tried to update the cart
      (submitting === SubmittingAction.CartModification &&
        // There's not currently a selection required for this asset
        !isSelectionRequired)
    ) {
      onClose();
      return;
    }
  }, [
    onClose,
    fetcher.data,
    fetcher.state,
    submitting,
    selectedAsset,
    isSelectionRequired,
  ]);

  const submittingFetcher =
    submitting === SubmittingAction.Purchase ? assetFetcher : fetcher;

  useEffect(() => {
    if (
      submittingFetcher.state == "idle" &&
      submittingFetcher?.data?.success &&
      submitting
    ) {
      setSubmitting(SubmittingAction.None);
    }
  }, [submittingFetcher.data, submittingFetcher.state, submitting]);

  const getBalanceText = () => {
    return (
      <span>
        You have{" "}
        <b className={styles.pointsText} style={{ color: pointsTextColor }}>
          {formatLoyaltyBalance(orgSettings, authenticatedUser?.loyaltyBalance)}{" "}
        </b>
        {orgSettings?.loyaltySettings?.balanceUnitName ?? "Balance"}
      </span>
    );
  };

  const getPointShop = () => {
    if (!assetInfo?.purchaseableAssets?.length) {
      return null;
    }

    return (
      <div className={styles.pointShop}>
        <div className={styles.header}>
          <span
            className={styles.title}
            style={{ color: titleFontColor, fontSize: titleFontSize }}
          >{`${pluralize.plural(capitalizeWords(promoName))} Shop`}</span>
        </div>
        <p className={styles.text} style={{ padding: "8px 0" }}>
          {`Select a ${pluralize.singular(
            promoName.toLowerCase()
          )} below to add to 'My ${pluralize.plural(
            capitalizeWords(promoName)
          )}'`}
        </p>
        {showTargetPoints && (
          <>
            <div
              className={styles.pointShopProgress}
              style={{ color: subtitleFontColor }}
            >
              <span style={{ fontWeight: 600 }}>
                {formatLoyaltyBalance(
                  orgSettings,
                  authenticatedUser?.loyaltyBalance
                )}
              </span>
              <span>{` / ${formatLoyaltyBalance(
                orgSettings,
                targetPoints
              )}`}</span>
            </div>
            <div className={styles.text}>
              {orgSettings?.loyaltySettings?.balanceUnitName ?? "Balance"} until
              your next {promoName}
            </div>
          </>
        )}
        <div className={styles.assetList}>
          {assetInfo?.purchaseableAssets?.map((asset, index) => (
            <AssetView
              key={index}
              asset={asset}
              onSelect={() => {
                setSelectedAsset(asset);
                setSubmitting(SubmittingAction.Purchase);

                const formData = new FormData();
                formData.append("assetId", asset.id!);
                assetFetcher.submit(formData, {
                  action: "/loyalty/purchaseAsset",
                  method: "post",
                });
              }}
              selected={false}
            />
          ))}
        </div>
      </div>
    );
  };
  const viewYourClubGoods = template.loyalty.text.viewYourClubGoods;

  const showRequirementSelection =
    !hideRequirementSelection && currentSelectionRequirement && menu;

  const getModalBody = () => {
    if (
      (submittingFetcher.state !== "idle" &&
        (submitting === SubmittingAction.AssetSelection ||
          submitting === SubmittingAction.Purchase)) ||
      loading
    ) {
      return (
        <div className={styles.modalBody}>
          <Spinner className={styles.spinner} />
        </div>
      );
    }

    if (showRequirementSelection) {
      const requirement = currentSelectionRequirement;

      // Find rules which need selections
      const productItems = getProductsList(menu).filter(
        (item) =>
          requirement.productSkus?.includes(item.productData.sku) ||
          requirement.productCodes?.includes(item.productCode)
      );

      return (
        <div className={styles.modalBody}>
          <div className={styles.assetNameSubtitle}>{requirement.subTitle}</div>
          <p
            className={styles.subtitle}
            style={{
              color: subtitleFontColor,
              fontSize: subtitleFontSize,
            }}
          >
            {requirement.text
              ? `Selection Required: ${requirement.text}`
              : "Selection Required"}
          </p>
          <div style={{ display: "none" }}>
            {requirement.productSkus?.join(", ")}
          </div>
          <div style={{ display: "none" }}>
            {requirement.productCodes?.join(", ")}
          </div>
          {!productItems.length ? (
            <div>
              <p style={{ marginTop: "16px" }}>
                No products from this menu match this promotion requirement.
              </p>
              <p style={{ marginTop: "8px" }}>
                Please select a different menu.
              </p>
            </div>
          ) : (
            <div className={styles.productGrid}>
              {productItems.map((item) => (
                <ProductItem
                  key={item.productCode}
                  product={item.productData}
                  onClick={() => {
                    const newCartLine = generateLine(item, menu?.code);
                    newCartLine.menuItemCode = null; // Don't show alternate products as they might not meet requirements for the promotion
                    if (cartIsValid(newCartLine.item)) {
                      addOrderLine(newCartLine);
                    } else {
                      setAddingLine(newCartLine);
                    }
                    setSubmitting(SubmittingAction.CartModification);
                  }}
                />
              ))}
            </div>
          )}
          <div style={{ flexGrow: 1 }}></div>
          <button
            className={styles.seeAllAssetsButton}
            onClick={() => setHideRequirementSelection(true)}
            aria-label={LABELS.CLOSE_DRAWER}
          >
            <BackArrowIcon />
            <div style={{ marginLeft: "16px" }}>{viewYourClubGoods}</div>
          </button>
        </div>
      );
    }

    if (selectedAsset && purchasedAsset) {
      return (
        <div className={styles.selectedAssetBody}>
          <p
            className={styles.subtitle}
            style={{ color: subtitleFontColor, fontSize: subtitleFontSize }}
          >
            {getBalanceText()} remaining
          </p>
          <p className={styles.pointShopProgress}>
            Nice choice! {pluralize.singular(capitalizeWords(promoName))} has
            been added to &apos;My{" "}
            {pluralize.plural(capitalizeWords(promoName))}&apos;. Would you like
            to use it now?
          </p>
          <AssetView
            asset={purchasedAsset}
            onSelect={() => {
              setHideRequirementSelection(false);
              setSubmitting(SubmittingAction.AssetSelection);
              setSelectedAsset(null);
              redeemPromotion(purchasedAsset.key!);
            }}
            onClose={() => {
              setSelectedAsset(null);
            }}
          />
        </div>
      );
    }

    if (assetInfo?.assets?.length === 0) {
      return (
        <div className={styles.modalBody}>
          <p
            className={styles.subtitle}
            style={{ color: subtitleFontColor, fontSize: subtitleFontSize }}
          >
            {getBalanceText()}
          </p>
          {showTargetPoints ? (
            <>
              <ProgressClubPoints
                currentPoints={authenticatedUser?.loyaltyBalance ?? 0}
              />
              <p className={styles.targetDescriptionText}>
                Clock 150 mpb (miles per burger) to score 15 Burger Bucks. Find
                your available club goods below
              </p>
            </>
          ) : (
            <p className={styles.text}>
              You have no {pluralize.plural(promoName.toLowerCase())} at the
              moment. Claim them using your{" "}
              {orgSettings?.loyaltySettings?.balanceUnitName ?? "Balance"}{" "}
              below.
            </p>
          )}
          {getPointShop()}
          {showTargetPoints && (
            <div className={styles.bottomText}>
              *One Club Good per order. Not available with other Club Goods,
              Burger Bucks or other BurgerFuel offer.
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={styles.modalBody}>
        <p
          className={styles.subtitle}
          style={{ color: subtitleFontColor, fontSize: subtitleFontSize }}
        >
          {getBalanceText()}
        </p>
        {showTargetPoints ? (
          <>
            <ProgressClubPoints
              currentPoints={authenticatedUser?.loyaltyBalance ?? 0}
            />
            <p className={styles.targetDescriptionText}>
              Clock 150 mpb (miles per burger) to score 15 Burger Bucks. Find
              your available club goods below
            </p>
          </>
        ) : (
          <p className={styles.text}>
            You&apos;ll find your available{" "}
            {pluralize.plural(promoName.toLowerCase())} below.{" "}
            <span className={styles.limitText}>
              One {pluralize.singular(promoName.toLowerCase())} per visit.
            </span>
          </p>
        )}
        <div className={styles.assetList}>
          {assetInfo?.assets?.map((asset, index) => (
            <AssetView
              key={index}
              asset={asset}
              onSelect={() => {
                setHideRequirementSelection(false);
                setSubmitting(SubmittingAction.AssetSelection);
                redeemPromotion(asset.key!);
              }}
              selected={orderSummary.redemptionKey === asset.key}
            />
          ))}
        </div>
        {getPointShop()}
        {showTargetPoints && (
          <div className={styles.bottomText}>
            *One Club Good per order. Not available with other Club Goods,
            Burger Bucks or other BurgerFuel offer.
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <ProductModal
        initLine={visible ? addingLine : null}
        onClose={() => setAddingLine(null)}
        isInsert
        isForPromoModal
        backdropRef={productModalRef}
      />
      <Dialog visible={visible} onClose={onClose}>
        <div className={styles.content} style={{ backgroundColor }}>
          <div
            className={clsx(
              selectedAsset ? styles.selectedAssetHeader : styles.modalHeader
            )}
          >
            {!selectedAsset && (
              <span
                className={styles.title}
                style={{
                  color: titleFontColor,
                  fontSize: titleFontSize,
                  paddingLeft: 24,
                }}
              >
                {orderSummary.redemptionKey && showRequirementSelection
                  ? `Select Your ${capitalizeWords(promoName)}`
                  : `My ${pluralize.plural(capitalizeWords(promoName))}`}
              </span>
            )}
            <CircleCloseButton color="#828282" onClick={onClose} />
          </div>
          {getModalBody()}
          <div className={styles.modalFooter}>
            {fetcherErrorMessage && (
              <div className={styles.errorMessage}>{fetcherErrorMessage}</div>
            )}
            {showAssetFetcherErrorMessage && (
              <div className={styles.errorMessage}>
                {assetFetcher.data?.message}
              </div>
            )}
            {error && <div className={styles.errorMessage}>{error}</div>}
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AssetModal;
